import type {
  CoinbaseEngUsersAccountManagementApiApiPendingInvite,
  CoinbaseEngUsersAccountManagementApiApiRoleDefinition,
  CoinbaseEngUsersAccountManagementApiApiRoleDetail,
} from '@cbhq/identity-api-hooks/account-management/types';
import { CoinbaseEngUsersAccountManagementApiApiRoleActivationState as RoleActivationState } from '@cbhq/identity-api-hooks/account-management/types';

import { ACCOUNT_MANAGEMENT_LE_ADMIN } from '../../../constants/roles';

import type { EntityRole, MemberRole } from './constants';

/**
 * FE-owned view models for the admin grace period ("admin access cooldown"):
 * pending roles, role protection and invite schedules. Member roles arrive as
 * `effectiveRoles` and `pendingRoles` from `normalizeWorkspaceMember`; this module
 * reads the invite and role definition API shapes.
 */

/** An entity role still waiting out its grace period: it takes effect at `effectiveAt`. */
export type PendingRole = EntityRole & { effectiveAt: Date };

export type RoleProtection = {
  /** How long a new grant of this role waits before it takes effect. */
  gracePeriodMs: number;
};

const HOUR_MS = 60 * 60 * 1000;

/**
 * Used when a role definition doesn't carry `gracePeriod` yet (backend not
 * shipped). Matches the backend manifest default (`gracePeriodDefault: "48h"`).
 */
export const FALLBACK_PROTECTED_ROLES: Partial<Record<MemberRole, RoleProtection>> = {
  [ACCOUNT_MANAGEMENT_LE_ADMIN]: { gracePeriodMs: 48 * HOUR_MS },
};

export type PendingInviteWithGracePeriod = CoinbaseEngUsersAccountManagementApiApiPendingInvite;

// TODO(IDFE-4029): read the generated type once the swagger has `grace_period`.
export type RoleDefinitionWithGracePeriod =
  CoinbaseEngUsersAccountManagementApiApiRoleDefinition & {
    /** Duration, e.g. "172800s" (proto Duration JSON) or "48h". */
    gracePeriod?: string | null;
  };

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

const DURATION_UNIT_MS: Record<string, number> = {
  s: 1000,
  m: 60 * 1000,
  h: HOUR_MS,
};

const DURATION_PATTERN = /^(\d+(?:\.\d+)?)([smh])$/;

/** Parses "172800s" / "48h" / "30m". Returns undefined for anything else. */
export function parseDurationMs(value: string | null | undefined): number | undefined {
  if (value == null) return undefined;
  const match = DURATION_PATTERN.exec(value.trim());
  if (!match) return undefined;
  return Number(match[1]) * DURATION_UNIT_MS[match[2]];
}

export function parseTimestamp(value: string | null | undefined): Date | undefined {
  if (value == null || value === '') return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

type ApiRoleDetail = CoinbaseEngUsersAccountManagementApiApiRoleDetail;

function isScheduled({ activationState }: ApiRoleDetail): boolean {
  return activationState === RoleActivationState.ROLE_ACTIVATION_STATE_SCHEDULED;
}

function hasActivationState({ activationState }: ApiRoleDetail): boolean {
  return (
    activationState != null &&
    activationState !== RoleActivationState.ROLE_ACTIVATION_STATE_UNSPECIFIED
  );
}

// ---------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------

/**
 * The member's pending roles whose `effectiveAt` is still after `now`, soonest
 * first. `pendingRoles` comes from `normalizeWorkspaceMember`.
 */
export function getPendingRoles(
  { pendingRoles }: { pendingRoles: readonly PendingRole[] },
  now: Date,
): PendingRole[] {
  return pendingRoles
    .filter(({ effectiveAt }) => effectiveAt > now)
    .sort((a, b) => a.effectiveAt.getTime() - b.effectiveAt.getTime());
}

/**
 * Whether "Revoke admin access" may run: CancelPendingRoleChange cancels every
 * pending role of the member at once, so each entity with a role still pending
 * at `now` must be manageable. False when nothing is pending.
 */
export function canCancelAllPendingRoles(
  pendingRoles: readonly Pick<PendingRole, 'legalEntityId' | 'effectiveAt'>[],
  now: Date,
  canManageEntity: (legalEntityId: string) => boolean,
): boolean {
  const livePendingRoles = pendingRoles.filter(({ effectiveAt }) => effectiveAt > now);
  return (
    livePendingRoles.length > 0 &&
    livePendingRoles.every(({ legalEntityId }) => canManageEntity(legalEntityId))
  );
}

/**
 * Grace period for a role: the definition's `gracePeriod` when present
 * (0 = explicitly unprotected), otherwise FALLBACK_PROTECTED_ROLES.
 */
export function getRoleProtection(
  roleId: MemberRole,
  roleDefinition?: RoleDefinitionWithGracePeriod,
): RoleProtection | null {
  const gracePeriodMs = parseDurationMs(roleDefinition?.gracePeriod);
  if (gracePeriodMs != null) {
    return gracePeriodMs > 0 ? { gracePeriodMs } : null;
  }
  return FALLBACK_PROTECTED_ROLES[roleId] ?? null;
}

/**
 * The longest grace period among the invited roles, or null when none is
 * protected. `protectionByRole` is typically the useRoles roleMap.
 */
export function getInviteProtection(
  roles: MemberRole[] | undefined,
  protectionByRole: Partial<Record<MemberRole, { protection?: RoleProtection }>>,
): RoleProtection | null {
  let longest: RoleProtection | null = null;
  for (const role of roles ?? []) {
    const protection = protectionByRole[role]?.protection;
    if (protection && (longest == null || protection.gracePeriodMs > longest.gracePeriodMs)) {
      longest = protection;
    }
  }
  return longest;
}

/**
 * When a pending invite's protected role becomes usable, or undefined when the
 * invite has no pending protected role or the window has already passed.
 * Reads the SCHEDULED `rolesDetailed` entries, for `role` when given; with
 * several, the latest. Invites whose details carry no activation state get
 * `createdAt + gracePeriod`, since the clock starts at invite time (per PRD).
 * The backend omits activation state wherever it has no pending-role reader.
 */
export function getPendingInviteEffectiveAt(
  invite: PendingInviteWithGracePeriod,
  protection: RoleProtection | null | undefined,
  now: Date,
  role?: MemberRole,
): Date | undefined {
  const details = (invite.rolesDetailed ?? []).filter(
    (detail) => role == null || detail.role === role,
  );

  let effectiveAt: Date | undefined;
  for (const detail of details.filter(isScheduled)) {
    const scheduledAt = parseTimestamp(detail.effectiveAt);
    if (scheduledAt && (effectiveAt == null || scheduledAt > effectiveAt)) {
      effectiveAt = scheduledAt;
    }
  }

  if (effectiveAt == null) {
    if (details.some(hasActivationState)) return undefined;
    const createdAt = parseTimestamp(invite.createdAt);
    if (!protection || protection.gracePeriodMs <= 0 || !createdAt) return undefined;
    effectiveAt = new Date(createdAt.getTime() + protection.gracePeriodMs);
  }

  return effectiveAt > now ? effectiveAt : undefined;
}
